import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import { authorizeWithOkta, getPageQuery } from '../../../helpers/oktaUtils';

/**
 * 登录页面
 * 显示 Nike Okta Login 按钮，点击后跳转到 Okta
 */
const Login: React.FC = () => {
  const { handleOktaLogin, authLoading, authError, isAuthenticated } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [oktaLoading, setOktaLoading] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isProcessingCallback, setIsProcessingCallback] = useState<boolean>(false);

  // 检查是否有 Okta 回调的 code 参数
  useEffect(() => {
    const params = getPageQuery();
    const code = params.code as string;
    const error = params.error as string;

    if (error) {
      // Okta 返回错误
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoginError((params.error_description as string) || error);
      return;
    }

    if (code && !isAuthenticated) {
      // 处理 Okta 回调
      setIsProcessingCallback(true);
      setOktaLoading(true);
      handleOktaLogin(code)
        .catch((err) => {
          setLoginError(err instanceof Error ? err.message : 'Okta login failed');
        })
        .finally(() => {
          setOktaLoading(false);
          setIsProcessingCallback(false);
        });
    }
  }, [location.search, handleOktaLogin, isAuthenticated]);

  // 如果已经认证，跳转到首页
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // 处理 Okta 登录按钮点击
  const handleOktaLoginClick = async () => {
    setOktaLoading(true);
    setLoginError(null);
    try {
      await authorizeWithOkta();
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Failed to redirect to Okta');
      setOktaLoading(false);
    }
  };

  // 如果正在处理 Okta 回调，显示加载页面
  if (isProcessingCallback || authLoading) {
    return (
      <PageWrapper isProtected={false} title='Login'>
        <Page className='p-0'>
          <div className='row h-100 align-items-center justify-content-center'>
            <div className='col-xl-4 col-lg-6 col-md-8 text-center'>
              <Card className='shadow-3d-dark'>
                <CardBody className='p-5'>
                  <Logo width={150} />
                  <div className='mt-4'>
                    <Spinner size='3rem' />
                  </div>
                  <div className='mt-4 h5'>Processing login...</div>
                  <div className='text-muted'>Please wait</div>
                </CardBody>
              </Card>
            </div>
          </div>
        </Page>
      </PageWrapper>
    );
  }

  // 显示登录页面
  return (
    <PageWrapper isProtected={false} title='Login' className='bg-dark'>
      <Page className='p-0'>
        <div className='row h-100 align-items-center justify-content-center'>
          <div className='col-xl-4 col-lg-6 col-md-8 text-center'>
            <Card className='shadow-3d-dark'>
              <CardBody className='p-5'>
                {/* Logo */}
                <div className='mb-4'>
                  <Logo width={180} />
                </div>

                {/* 标题 */}
                <div className='h4 fw-bold mb-2'>Tmall Audience</div>
                <div className='text-muted mb-4'>Sign in to continue</div>

                {/* 显示错误信息 */}
                {(loginError || authError) && (
                  <Alert color='danger' isLight icon='Error' isDismissible className='text-start'>
                    {loginError || authError}
                  </Alert>
                )}

                {/* Nike Okta Login 按钮 */}
                <Button
                  color='primary'
                  size='lg'
                  className='w-100 py-3'
                  icon='Security'
                  isDisable={oktaLoading}
                  onClick={handleOktaLoginClick}>
                  {oktaLoading ? (
                    <>
                      <Spinner isSmall inButton isGrow />
                      Redirecting...
                    </>
                  ) : (
                    'Nike Okta Login'
                  )}
                </Button>
              </CardBody>
            </Card>
          </div>
        </div>
      </Page>
    </PageWrapper>
  );
};

export default Login;
